import classNames from 'classnames';
import React from 'react';
import { useTranslate } from '../../mixins/translate/translate';
import { usePatchedDropdownMenu } from '../../utils/usePatchedDropdownMenu';

export interface ICategory {
    id: string;
    name: string;
}

export interface IUnit {
    id: string;
    name: string;
    code: string;
    isDefault: boolean;
    replacementIndex?: number;
    location?: string;
    assetsCount: number;
    conditionsCount: number;
}

export interface IElement {
    id: string;
    name: string;
    categoryId: string;
    code: string;
    isDefault: boolean;
    replacementIndex?: number;
    location?: string;
    assetsCount: number;
    conditionsCount: number;
    units: IUnit[];
}

interface IElementProps {
    element: IElement;
    submitting: boolean;
    isNen: boolean;
    inspectionStandardId: string;
    removeElement: (element: IElement) => void;
    removeUnit: (unit: IUnit) => void;
    handleEditElementClicked: (id: string) => void;
    handleAddUnitClicked: (id: string, code: string) => void;
    handleEditUnitClicked: (id: string, code: string) => void;
}

export function ElementRow(props: IElementProps) {
    const translate = useTranslate();
    const { buttonProps, itemProps, isOpen, setIsOpen } = usePatchedDropdownMenu(3);
    const classes = classNames('btn-group', { open: isOpen });

    const removeUnitHandler = (unit: any) => {
        const message = translate('common.confirmDelete', unit.name);
        const isDeletionConfirmed = window.confirm(message);

        if (isDeletionConfirmed) {
            props.removeUnit(unit);
        }
    };

    const removeElementClickHandler = () => {
        const message = translate('common.confirmDelete', props.element.name);
        const isDeletionConfirmed = window.confirm(message);

        if (isDeletionConfirmed) {
            props.removeElement(props.element);
        }

        setIsOpen(false);
    };

    const editElementClickHandler = () => {
        setIsOpen(false);
        props.handleEditElementClicked(props.element.id);
    };

    const addUnitClickHandler = () => {
        setIsOpen(false);
        props.handleAddUnitClicked(props.element.id, props.element.code);
    };

    return (
        <>
            <thead key={props.element.id}>
                <tr className="primary">
                    <th>{props.element.code}</th>
                    <th>{props.element.name}</th>
                    <th className="text-right">
                        <div className={classes}>
                            <button
                                {...buttonProps}
                                className="btn btn-primary btn-sm fs12 fw500 dropdown-toggle ph10"
                                data-toggle="dropdown"
                                disabled={props.submitting}
                                type="button"
                            >
                                {translate('common.actions')}
                                <span className="caret"></span>
                            </button>
                            <ul className="dropdown-menu pull-right" role="menu">
                                <li {...itemProps[0]}>
                                    <a className="cursor edit-element" onClick={editElementClickHandler}>
                                        {translate('common.edit')}
                                    </a>
                                </li>
                                <li {...itemProps[1]} className={classNames({ hidden: props.element.units.length > 0 })}>
                                    <a className="cursor" onClick={removeElementClickHandler}>
                                        {translate('common.remove')}
                                    </a>
                                </li>
                                <li className="divider"></li>
                                <li {...itemProps[2]}>
                                    <a className="cursor edit-unit" onClick={addUnitClickHandler}>
                                        {translate('common.addItem', translate('common.unit'))}
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </th>
                </tr>
            </thead>
            <tbody key={`${props.element.id}tbody`}>
                {props.element.units.map((unit) => (
                    <tr key={unit.id}>
                        <td>{unit.code}</td>
                        <td>{unit.name}</td>
                        <td className="text-right">
                            <button
                                type="button"
                                onClick={() => removeUnitHandler(unit)}
                                className="btn btn-default btn-sm fs12 ml5"
                                title={translate('common.remove')}
                            >
                                <span className="fa fa-trash-o" />
                            </button>
                            <button
                                type="button"
                                onClick={() => props.handleEditUnitClicked(props.element.id, unit.id)}
                                className="btn btn-default btn-sm fs12 edit-unit ml5"
                                title={translate('common.edit')}
                            >
                                <span className="fa fa-edit" />
                            </button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </>
    );
}
